import React, { useEffect, useMemo, useState } from "react";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useGetPrimaryCollectorByStateAndCountyQuery, useSearchAppraisalRollsQuery } from "../../data/appraisalRoll/appraisalRollsApi";
import Modal from "../../../components/common/form/Modal";
import { populateStateAndCounty } from "../../../helpers/utils/OrderDetailUtils";
import { useUpdatePropertyResultsMutation } from "../ordersApi";
import ButtonTray from "../../../components/common/form/ButtonTray";
import CollectorSearchInputWrapper from "../../../components/common/datatable/filter/CollectorSearchInputWrapper";
import SelectField from "../../../components/common/form/SelectField";

interface PropertyResultPickerModalProps {
    show: boolean;
    onHide?: () => void;
    properties?: { id: number; name: string; value: string }[];
    setShow: (show: boolean) => void;
    onSelect?: (selectedProperty: { id: number; name: string; value: string }) => void;
    orderData?: any
    taxAccountNumber: any
    setTaxAccountNumber?: any
    address: any
    setAddress: any
    legalDescription: any
    setLegalDescription: any
    ownerName: any
    setOwnerName: any
    orderId: any
    propertyId: any
    state?: any
    county?: any
}


const PropertyResultPickerModal: React.FC<PropertyResultPickerModalProps> = ({ show, onHide, setShow, properties, onSelect, state, county,
    orderData, taxAccountNumber, setTaxAccountNumber, address, setAddress, legalDescription, setLegalDescription, ownerName, setOwnerName, orderId, propertyId
}) => {

    const [preDefaultCheck, setPreDefaultCheck] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(false);
    const [disable, setDisable] = useState(true);
    const primaryCollectorData = useGetPrimaryCollectorByStateAndCountyQuery({
        stateAbbr: state, countyName: county
    });
    const [resetDefault, setResetDefault] = useState(false);
    const [collectorValue, setCollectorValue] = useState<string | null>(null);
    const [collectorId, setCollectorId] = useState<string | null>(null);

    const [collectorTagValue, setCollectorTagValue] = useState<string | null>(null);
    const [legal, setLegal] = useState(null);
    const [addressLine1, setAddressLine1] = useState(null);
    const [subdivisionName, setSubdivisionName] = useState(null);
    const [stateAbbr, setStateAbbr] = useState<any>();
    const [countyName, setCountyName] = useState(null);
    const [sellerName, setSellerName] = useState(null);
    const [taxId, setTaxId] = useState(null);
    const [geoId, setGeoId] = useState(null);
    const [updateApi, updateApiRes] = useUpdatePropertyResultsMutation();
    const [suitAddress1, setSuitAddress1] = useState<any>();



    useEffect(() => {
        if (resetDefault) {
            setCollectorValue(null);
            setTaxAccountNumber("");
            setAddress("");
            setLegalDescription("");
            setOwnerName("");
        }
        setResetDefault(false)
    }, [resetDefault]);

    useEffect(() => {
        if (orderData != null && suitAddress1 != null && suitAddress1 !== "") {
            const index = orderData.data?.properties?.findIndex((el: any) => el.id === propertyId);

            if (index > -1) {
                const property = orderData.data?.properties[index];
                setTaxAccountNumber(property?.taxIdentifier || "");
                setAddress(property?.addressLine1 || "");
                setLegalDescription(property?.legalDescription || "");
                setOwnerName(property?.sellerName || "");
            } else {
                setTaxAccountNumber("");
                setAddress("");
                setLegalDescription("");
                setOwnerName("");
            }
        }
    }, [suitAddress1]);


    useEffect(() => {

        const selectedProperty = orderData.data?.properties.find((el: any) => el.id === propertyId);
        setSuitAddress1({
            id: selectedProperty?.id,
            label: `${selectedProperty?.sequence}. ${selectedProperty?.addressLine1}`,
            value: selectedProperty?.addressLine1,
        });


    }, [orderData?.data]);

    useMemo(() => {
        if (primaryCollectorData?.data && !resetDefault) {
            setCollectorValue(primaryCollectorData?.data?.name);
            setCollectorTagValue(primaryCollectorData?.data?.name);
            setCollectorId(primaryCollectorData?.data?.id)
            setPreDefaultCheck(true);
        }
    }, [primaryCollectorData]);

    const setPropertyData = async (row: any) => {
        const [record] = row instanceof Set ? Array.from(row) : [row];
        if (!record) return;
        setSellerName(record.ownerName);
        setLegal(record.legalDescription);
        setAddressLine1(record.situsAddress);
        setSubdivisionName(record.subdivisionName);
        setGeoId(record.geographicId);
        setTaxId(record.taxAccountNumber);
        try {
            const { state, countyName } = await populateStateAndCounty(record.collectorId, false);
            setStateAbbr(state);
            setCountyName(countyName);
        } catch (error) {
            console.error("Error populating state and county:", error);
        }

        setSelectedProperty(true);
        setDisable(false)
    };



    function selectPropertyResult(props: any) {

        return (
            <>
                <span id={props.row.original.id + "1"}>{props.value}</span>
            </>
        )
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const formData = {
            "legalDescription": legal,
            "address": addressLine1,
            "subdivisionName": subdivisionName,
            "parsedAddress": {
                "state": stateAbbr,
                "county": countyName
            },
            "primaryOwner": sellerName,
            "taxId": taxId,
            "geoId": geoId,
            "propertyResultSource": "PICKER"
        }
        setDisable(true);
        setSelectedProperty(false);
        try {
            await updateApi({
                orderId,
                propertyId,
                propertyResult: formData
            });
        } catch (error) {
            console.error("Update failed:", error);
        } finally {
            setDisable(false);
            setShow(false)
        }
    }

    const columns: DataTableColumn[] = [
        {
            label: "Tax Year", accessor: "taxYear",
            cell: (props: any) => {
                return selectPropertyResult(props);
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"number"} placeholder={"Tax Year..."} name={"taxYear"}
                    accessor={"taxYear"} />,
                overrideFilterLabel: "Tax Year",
                overrideFilterAccessor: "taxYear"
            }]
        },
        {
            label: "Tax ID", accessor: "taxAccountNumber",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Tax Account No..."}
                    name={"taxAccountNumber"} accessor={"taxAccountNumber"} defaultValue={taxAccountNumber}
                />,
                overrideFilterLabel: "Tax Account No.",
                overrideFilterAccessor: "taxAccountNumber"
            }]
        },
        {
            label: "Geo ID", accessor: "geographicId",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Geo Id..."}
                    name={"geographicId"} accessor={"geographicId"} />,
                overrideFilterLabel: "Geo Id",
                overrideFilterAccessor: "geographicId"
            }]
        },
        {
            label: "Collector",
            accessor: "collectorName",
            overrideSortColumn: "collectorId",
            filterOptions: [{
                filterComponent: <CollectorSearchInputWrapper value={collectorValue || ''} accessor="collectorId" />,
                overrideFilterAccessor: "collectorId",
                overrideFilterLabel: "Collector",
            }]
        },

        {
            label: "Address", accessor: "situsAddress",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Address..."}
                    name={"situsAddress"} accessor={"situsAddress"} defaultValue={address}
                />,
                overrideFilterLabel: "Situs Address",
                overrideFilterAccessor: "situsAddress"
            }]
        },
        {
            label: "Subdivision", accessor: "subdivisionName",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Subdivision Name..."}
                    name={"subdivision"} accessor={"subdivisionName"} />,
                overrideFilterLabel: "Subdivision Name",
                overrideFilterAccessor: "subdivisionName"
            }]
        },

        {
            label: "Sub Code", accessor: "subdivisionCode",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Sub Code..."}
                    name={"subdivisionCode"} accessor={"subdivisionCode"} />,

                overrideFilterAccessor: "subdivisionCode"
            }]
        },

        {
            label: "Owner", accessor: "ownerName",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Owner Name..."}
                    name={"ownerName"} accessor={"ownerName"}
                />,
                overrideFilterLabel: "Owner Name",
                overrideFilterAccessor: "ownerName"
            }]
        },

        {
            label: "Legal", accessor: "legalDescription",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Legal Description..."}
                    name={"legalDescription"} accessor={"legalDescription"} defaultValue={legalDescription}
                />,
                overrideFilterLabel: "Legal Description",
                overrideFilterAccessor: "legalDescription"
            }]
        },
    ];

    const handleRowClicked = (row: any) => {
        setPropertyData(row);
    };

    return (
        <Modal
            title={"Property Result Picker"}
            show={show}
            setShow={setShow}
            className="w-4/5  bg-text-0 rounded-lg shadow-lg"
            fields={
                <>
                    <div className="modal-body">
                        <SelectField
                            placeholder='No Property Selected...'
                            value={suitAddress1}
                            options={Array.isArray(orderData.data?.properties) ? orderData.data.properties?.filter((el: any) => el.id === propertyId).map((item: any) => ({
                                value: item.id,
                                label: `${item?.sequence}. ${item?.addressLine1}`
                            })) : []}
                            onChange={(selectedOption: any) => {
                                setSuitAddress1(selectedOption);
                                setResetDefault(true);
                            }}
                            required
                            hideLabel={true}
                        />
                        {collectorId && (
                            <DataTable
                                columns={columns}
                                actions={{
                                    fetchAction: useSearchAppraisalRollsQuery,
                                }}
                                defaultFilter={{
                                    pageNumber: 0,
                                    pageSize: 5,
                                    setProperty: "function",
                                    sort: "collectorId",
                                    sortOrder: "DESCENDING",
                                    collectorId: collectorId,
                                    taxAccountNumber: taxAccountNumber,
                                    situsAddress: address,
                                    legalDescription: legalDescription,
                                }}
                                defaultFilterFormExpanded={true}
                                defaultSortOptions={{
                                    accessor: 'collectorId',
                                    direction: 'desc'
                                }}
                                renderOptions={{
                                    skipFilterButtonRender: true
                                }}
                                appendFilterToUrl={false}
                                rowSelectionOptions={{
                                    enableRowSelection: true,
                                    onSelectionChange: (selectedRows) => {
                                        handleRowClicked(selectedRows)
                                    },
                                }}
                                setResetDefaultFilter={setResetDefault}
                                resetDefaultFilter={resetDefault}
                                searchOnApply={true}
                            />
                        )}
                    </div>

                    <div className="mt-4 border-t border-t-surface-5 flex justify-end
 ">
                        <ButtonTray
                            buttons={[
                                {
                                    buttonProps: {
                                        btnText: "Cancel",
                                        onClick: () => setShow(false),
                                        type: "close",
                                    },
                                    buttonType: "button"
                                },
                                {
                                    buttonProps: {
                                        btnText: "Update",
                                        type: "submit",
                                        onClick: handleSubmit,
                                        isLoading: updateApiRes.isLoading,
                                        isDisabled: disable
                                    },
                                    buttonType: "button",
                                }
                            ]}
                            align={"end"}
                            gap={2}
                        />

                    </div>
                </>

            }
        />

    );
}

export default PropertyResultPickerModal;